import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { HERO_IMAGE } from "@/lib/siteContent";

export const Hero = () => {
  const { t } = useT();
  return (
    <section id="top" className="relative overflow-hidden isolate min-h-[640px] sm:min-h-[720px]" aria-labelledby="hero-title">
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt={t.hero.imageAlt}
          className="h-full w-full object-cover"
          fetchpriority="high"
          loading="eager"
        />
        {/* Light scrim: just enough on the left for white text, so the bright
            summer photo keeps its punch. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, hsl(24 18% 8% / 0.72) 0%, hsl(24 18% 8% / 0.55) 38%, hsl(24 18% 8% / 0.18) 70%, hsl(24 18% 8% / 0.06) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, hsl(24 18% 8% / 0.12) 0%, hsl(24 18% 8% / 0.00) 40%, hsl(24 18% 8% / 0.35) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 container-x pt-24 pb-28 sm:pt-28 sm:pb-32 lg:pt-36 lg:pb-40">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="max-w-3xl text-primary-foreground"
          style={{ textShadow: "0 1px 2px rgb(0 0 0 / 0.45), 0 8px 28px rgb(0 0 0 / 0.35)" }}
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs uppercase tracking-[0.18em] text-white backdrop-blur" data-testid="hero-kicker">
            <MapPin className="h-3.5 w-3.5" aria-hidden />
            {t.hero.kicker}
          </div>
          <h1
            id="hero-title"
            className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight text-white"
            data-testid="hero-title"
          >
            {t.hero.title}
          </h1>
          <p className="mt-5 max-w-xl text-base sm:text-lg text-white/90 leading-relaxed" data-testid="hero-subtitle">
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Button asChild size="lg" className="h-12 rounded-xl px-6 text-sm" data-testid="hero-primary-cta">
              <a href="#kontakt" className="inline-flex items-center gap-2">
                {t.hero.primaryCta}
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              variant="secondary"
              size="lg"
              className="h-12 rounded-xl px-6 text-sm bg-white/10 text-white border border-white/25 hover:bg-white/20"
              data-testid="hero-secondary-cta"
            >
              <a href="#ubytovanie">{t.hero.secondaryCta}</a>
            </Button>
          </div>

          <div className="mt-8 flex items-center gap-2 text-sm text-white/85" data-testid="hero-trust-line">
            <MapPin className="h-4 w-4" aria-hidden />
            <span>{t.hero.trustLine}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
